import { useEffect, useRef, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

const SESSION_DURATION_MS = 60 * 60 * 1000; // 1 hour
const WARNING_BEFORE_MS = 5 * 60 * 1000; // 5 minutes before expiry
const WARNING_TIME_MS = SESSION_DURATION_MS - WARNING_BEFORE_MS; // 55 minutes
const WARNING_TOAST_ID = 'session-expiring-warning';
const warnedSessions = new Set<string>();

export function useSessionExpiryWarning() {
  const { session, signOut } = useAuth();
  const warningTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const expiryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const hasWarnedRef = useRef(false);
  const sessionKey = session?.access_token ? `session-warning-${session.access_token}` : null;

  const clearTimeouts = useCallback(() => {
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
      warningTimeoutRef.current = null;
    }
    if (expiryTimeoutRef.current) {
      clearTimeout(expiryTimeoutRef.current);
      expiryTimeoutRef.current = null;
    }
  }, []);

  const showWarning = useCallback(() => {
    const warningKey = sessionKey ?? 'default-session';
    if (warnedSessions.has(warningKey)) return;

    if (hasWarnedRef.current) return;
    warnedSessions.add(warningKey);
    hasWarnedRef.current = true;
    if (sessionKey) {
      sessionStorage.setItem(sessionKey, '1');
    }

    toast.warning('Session Expiring Soon', {
      description: 'Your session will expire in 5 minutes. Please save your work.',
      duration: 30000, // Show for 30 seconds
      id: WARNING_TOAST_ID, // Deduplicate if multiple triggers happen close together
      action: {
        label: 'Stay Logged In',
        onClick: () => {
          // Refresh the page to get a new session token
          window.location.reload();
        },
      },
    });
  }, [sessionKey]);

  useEffect(() => {
    if (!session?.expires_at) {
      clearTimeouts();
      hasWarnedRef.current = false;
      return;
    }

    // Check if we've already warned for this specific session token
    const alreadyWarned = sessionKey ? sessionStorage.getItem(sessionKey) === '1' : false;

    // Calculate time until expiry
    // expires_at is a Unix timestamp (seconds), need to convert to milliseconds
    const expiresAtSeconds = typeof session.expires_at === 'number' 
      ? session.expires_at 
      : parseInt(session.expires_at as unknown as string, 10);
    const expiresAtMs = expiresAtSeconds * 1000;
    const now = Date.now();
    const timeUntilExpiry = expiresAtMs - now;
    const timeUntilWarning = timeUntilExpiry - WARNING_BEFORE_MS;

    // Reset warning flag when session changes
    hasWarnedRef.current = alreadyWarned;
    clearTimeouts();

    // If session is already expired or about to expire
    if (timeUntilExpiry <= 0) {
      signOut();
      return;
    }

    // If we're already past the warning time, show warning immediately (only once per session)
    if (!alreadyWarned && timeUntilWarning <= 0 && timeUntilExpiry > 0) {
      showWarning();
    } else if (timeUntilWarning > 0 && !alreadyWarned) {
      // Schedule warning (only if it hasn't already been shown for this session)
      warningTimeoutRef.current = setTimeout(showWarning, timeUntilWarning);
    }

    // Schedule auto-logout
    expiryTimeoutRef.current = setTimeout(() => {
      toast.error('Session Expired', {
        description: 'Your session has expired. Please log in again.',
        duration: 5000,
      });
      signOut();
    }, timeUntilExpiry);

    return clearTimeouts;
  }, [session?.expires_at, signOut, clearTimeouts, showWarning]);
}
